import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type SessionService from 'bcl/services/session';
import { on } from '@ember/modifier';
import sort from 'bcl/helpers/sort';
import displayDate from 'bcl/helpers/display-date';
import { LinkTo } from '@ember/routing';
import { fn } from '@ember/helper';
import SupplierModel from 'bcl/models/supplier';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';

interface BasicDataSuppliersSignature {
  // The arguments accepted by the component
  Args: {
    model: SupplierModel[];
    newOrder: (supl: SupplierModel) => void;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

function filterSpecialSupplier(suppliers: SupplierModel[]): SupplierModel[] {
  return suppliers.filter((supplier) => supplier.additional !== 'special');
}

export default class BasicDataSuppliersComponent extends Component<BasicDataSuppliersSignature> {
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare router: RouterService;

  @action
  create() {
    const newModel = this.store.createRecord('supplier', {
      company: this.session.company,
    });
    this.router.transitionTo('auth.basic-data.supplier', newModel);
  }
  @action
  edit(item: SupplierModel) {
    this.router.transitionTo('auth.basic-data.supplier', item.id);
  }
  <template>
    <h2
      id="suppliers"
      class="scroll-mt-10 text-xl font-semibold leading-7 text-gray-900"
    >Lieferanten</h2>
    <div class="px-4 lg:px-8">
      <div class="sm:flex sm:items-center">
        <div class="sm:flex-auto">

        </div>
        <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            {{on "click" this.create}}
            class="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >Neuen Lieferanten anlegen</button>
        </div>
      </div>
      <div class="mt-8 flow-root">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
            <table class="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                  >Name</th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >Status</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >laufende Bestellungen</th>

                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                {{#each (sort (filterSpecialSupplier @model) "name") as |item|}}
                  <tr class="even:bg-gray-50">
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                    >{{item.name}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{if item.active "Aktiv" "Deaktiviert"}}</td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >{{#each (sort item.openOrders "delivery_date") as |oo|}}
                        Bestellung zum
                        {{displayDate oo.delivery_date}}
                        <LinkTo
                          @route="auth.orders.accept"
                          @model={{oo.id}}
                          class="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >annehmen<span class="sr-only">,

                            {{oo.supplier.name}}</span></LinkTo>
                      {{else}}
                        keine
                      {{/each}}
                    </td>

                    <td
                      class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                    >
                      <button
                        type="button"
                        {{on "click" (fn this.edit item)}}
                        class="text-indigo-600 hover:text-indigo-900"
                      >bearbeiten<span class="sr-only">,
                          {{item.name}}</span></button>

                      <button
                        type="button"
                        {{on "click" (fn @newOrder item)}}
                        class="text-indigo-600 hover:text-indigo-900 ml-4"
                      >neue freie Bestellung<span class="sr-only">
                          für
                          {{item.name}}</span></button>
                    </td>
                  </tr>
                {{/each}}

              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
}
